import React, { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../../lib/utils'

// Generic centered modal — the shape we want consistently across the
// app whenever something needs to interrupt the page (how-to-play,
// update prompt, future confirmations). Owns the boring bits:
//
//   - Portal into <body> so the dialog sits above any local
//     stacking context the calling page might have.
//   - Scrim + click-outside (opt-out via `closeOnScrim={false}`).
//   - ESC key handling (opt-out via `closeOnEsc={false}`).
//   - Scroll lock on `[data-shell-scroll]` (the app's actual scroll
//     container) AND <body> as a defensive fallback for iOS PWA
//     standalone where the inner-container lock has been known to
//     leak on certain gestures.
//   - Layered z-indexes: scrim at 40, dialog at 50 — same numbers
//     ProfileSheet and the article overlay use, so a modal opened on
//     top of either still wins. The bottom tab bar (z-30) stays
//     interactive only if the calling code wants it that way; the
//     scrim covers everything below z-40 by default.
//
// Width: w-[calc(100%-1.5rem)] up to max-w-md by default so it feels
// "page-sized" on mobile. Override via `className` for wider or
// narrower content.
export default function Modal({
  open,
  onClose,
  children,
  className,
  ariaLabel,
  ariaLabelledBy,
  closeOnScrim = true,
  closeOnEsc = true,
}) {
  useEffect(() => {
    if (!open) return undefined
    const shell = document.querySelector('[data-shell-scroll]')
    const prevShell = shell?.style.overflow
    const prevBody = document.body.style.overflow
    if (shell) shell.style.overflow = 'hidden'
    document.body.style.overflow = 'hidden'
    return () => {
      if (shell) shell.style.overflow = prevShell || ''
      document.body.style.overflow = prevBody || ''
    }
  }, [open])

  useEffect(() => {
    if (!open || !closeOnEsc) return undefined
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, closeOnEsc, onClose])

  if (!open || typeof document === 'undefined') return null

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
        onClick={closeOnScrim ? onClose : undefined}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        className={cn(
          'fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50',
          'w-[calc(100%-1.5rem)] max-w-md',
          'bg-card border border-border rounded-xl shadow-xl',
          'p-4 sm:p-5',
          className,
        )}
      >
        {children}
      </div>
    </>,
    document.body,
  )
}
